import { useQuery } from '@apollo/client/react';
import { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReferenceDataProvider, UIProvider } from '@insio/ui-toolkit';
import { useAuth } from '@/features/auth';
import { isPublicRoute } from '@/features/auth/constants/routes';
import { GetCountriesDocument, GetCurrenciesDocument, GetProfileDocument } from '@/graphql';

/**
 * Wires the single UIProvider with values derived from i18n + profile preferences,
 * and hydrates ReferenceDataProvider with country/currency lists from GraphQL.
 * - `locale` (translations) follows the current `i18n.language`.
 * - `dateFormat` / `numberFormat` / `timeZone` come from the authenticated
 *   user's preferences (user-level wins over account-level) with Czech defaults
 *   on public/unauthenticated routes.
 * - `countries` / `currencies` are fetched once (cache-first) and shared across
 *   any FieldEditor / modal that needs them.
 */
export function AppUIProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { t, i18n } = useTranslation('ui');
  const { isAuthenticated } = useAuth();

  const isPublicRoutePath = isPublicRoute(location.pathname);
  const skipAuthedQueries = !isAuthenticated || isPublicRoutePath;

  const { data: profileData } = useQuery(GetProfileDocument, {
    fetchPolicy: 'cache-first',
    skip: skipAuthedQueries
  });
  const { data: countriesData } = useQuery(GetCountriesDocument, {
    fetchPolicy: 'cache-first',
    skip: skipAuthedQueries
  });
  const { data: currenciesData } = useQuery(GetCurrenciesDocument, {
    fetchPolicy: 'cache-first',
    skip: skipAuthedQueries
  });

  const currentPrefs = profileData?.profile?.currentPreferences;
  // The account resolves the reporting currency, falling back to the accounting currency.
  const account = profileData?.profile?.account;
  const timeZone = currentPrefs?.timeZone ?? 'Europe/Prague';

  return (
    <UIProvider
      i18n={{ t }}
      locale={i18n.language}
      dateFormat={currentPrefs?.dateFormat ?? undefined}
      numberFormat={currentPrefs?.numberFormat ?? undefined}
      timeZone={timeZone}
      accountingCurrencyCode={account?.accountingCurrencyCode ?? null}
      reportingCurrencyCode={account?.reportingCurrencyCode ?? null}
    >
      <ReferenceDataProvider
        countries={countriesData?.countries ?? []}
        currencies={currenciesData?.currencies ?? []}
      >
        {children}
      </ReferenceDataProvider>
    </UIProvider>
  );
}
